import { booleanAttribute, Component, computed, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ReactiveFormsModule } from '@angular/forms';

import { AppMaterialModule } from '../../../material.module';
import { autoComplete_text, autoComplete_value } from '../../models/auto-complete.model';
import { hintAlign, THintAlign, TToggleAppearance, toggleAppearance } from '../../models/form-field-options';
import { IToggleOption, toggleOption_icon } from '../../models/toggle-option.model';
import { _BaseControlComponent } from '../../components/_base-control.component';
import { IconComponent } from 'src/app/features/shared/icons/container/icon.component';
import { InputControlComponent } from '../input-control/input-control.component';

// Waarde van een optie = itemId (of de string zelf): mat-button-toggle-group vergelijkt met ===
// en kent geen compareWith, dus objecten als waarde zouden de keuze nooit oplichten.
// Een optie kan een icoon vóór de tekst hebben (IToggleOption.icon).
//
// Label/hint/fout als RadioButtonControlComponent (choice-field.scss), maar zonder veldvlak (transparant, geen
// onderlijn) en altijd op één rij; readonly alleen de gekozen optie(s) als readonly tekstveld (InputControlComponent).
// [framed]="false": alleen de groep, zonder zichtbaar label (werkbalk, dialoog-schakelaar).
@Component({
    selector: 'app-button-toggle-control',
    templateUrl: './button-toggle-control.component.html',
    styleUrls: [
        '../../control.scss',
        '../../choice-field.scss',
        './button-toggle-control.component.scss',
    ],
    standalone: true,
    host: {
        '[class.readonly]': 'readonly$$()'
    },
    imports: [
        AppMaterialModule,
        IconComponent,
        InputControlComponent,
        ReactiveFormsModule,
    ],
})
export class ButtonToggleControlComponent
    extends _BaseControlComponent
    implements OnInit, OnDestroy
{
    input_appearance$$                   = input<TToggleAppearance>(toggleAppearance.standard, { alias: 'appearance' });
    input_disableRipple$$                = input(false,                                        { transform: booleanAttribute, alias: 'disableRipple' });
    input_framed$$                       = input(true,                                         { transform: booleanAttribute, alias: 'framed' });
    input_hideMultipleSelectionIndicator$$ = input(false,                                      { transform: booleanAttribute, alias: 'hideMultipleSelectionIndicator' });
    input_hideSingleSelectionIndicator$$ = input(false,                                        { transform: booleanAttribute, alias: 'hideSingleSelectionIndicator' });
    // Hint onder het veld, zelfde betekenis als bij de form-field-controls (_MatFormFieldComponent).
    input_hint$$                         = input<string>('',                                   { alias: 'hint' });
    input_hintAlign$$                    = input<THintAlign>(hintAlign.start,                  { alias: 'hintAlign' });
    input_label$$                        = input<string>('',                                   { alias: 'label' });
    input_list$$                         = input<IToggleOption[] | string[]>([],               { alias: 'list' });
    input_multiple$$                     = input(false,                                        { transform: booleanAttribute, alias: 'multiple' });
    input_vertical$$                     = input(false,                                        { transform: booleanAttribute, alias: 'vertical' });

    // Alleen bij een keuze door de gebruiker (niet bij setValue van buitenaf).
    out_valueChange = output<any>({ alias: 'valueChange' });

    override attributes$$        = this._input_attributes$$;
    override autofocus$$         = this._input_autofocus$$;
    override control$$           = this._input_control$$;
    override controlName$$       = this._input_controlName$$;
    override disabled$$          = this._input_disabled$$;
    override errorShow$$         = this._input_errorShow$$;
    override errorStateMatcher$$ = this._input_errorStateMatcher$$;
    override id$$                = this._input_id$$;
    override lockOnLoad$$        = this._input_lockOnLoad$$;
    override placeholder$$       = this._input_placeholder$$;
    override readonly$$          = this._input_readonly$$;
    override subscriptHide$$     = this._input_subscriptHide$$;
    override tabIndex$$          = this._tabIndex$$;
    override value$$             = this._input_value$$;
    override valueReset$$        = this._input_valueReset$$;

    override get label() { return this.input_label$$(); }

    // mat-button-toggle verwacht een getal; de base-control houdt tabIndex als string bij.
    tabIndexNumber$$ = computed(() => Number(this.tabIndex$$()));

    readonly itemIcon = toggleOption_icon;
    readonly itemText = autoComplete_text;
    readonly itemValue = autoComplete_value;
    protected readonly _hintAlign = hintAlign;

    // Tekst van de gekozen optie(s) (readonly-weergave; bij multiple komma-gescheiden). Volgt de
    // control-waarde, zodat bv. een reset of later geladen waarde meteen zichtbaar is.
    readonly selectedText$$ = signal<string>('');
    private __valueSubscr: Subscription | null = null;

    override ngOnInit(): void {
        super._ngOnInit();

        this.__valueSubscr = this.control$$()?.valueChanges
            .pipe(startWith(this.control$$()?.value))
            .subscribe(value => this.selectedText$$.set(this.__text_get(value))) ?? null;
    }

    private __text_get(value: unknown): string {
        const __values = Array.isArray(value) ? value : [value];
        return this.input_list$$()
            .filter(item => __values.includes(this.itemValue(item)))
            .map(item => this.itemText(item))
            .join(', ');
    }

    emit_valueChange(value: any): void {
        this.out_valueChange.emit(value);
    }

    override ngOnDestroy(): void {
        this.__valueSubscr?.unsubscribe();
        super._ngOnDestroy();
    }
}
